import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Phone,
  ArrowRight,
  BadgeCheck,
  Handshake,
  KeyRound,
  Recycle,
  MapPin,
  ShieldCheck,
  FileCheck2,
} from "lucide-react";
import { api } from "@/lib/api";
import { SITE, HERO_IMAGE, ABOUT_IMAGE, formatPrice } from "@/lib/site";
import { Reveal, MaskedLine, SectionHeading } from "@/components/Reveal";
import Marquee from "@/components/Marquee";
import VehicleSubmissionForm from "@/components/VehicleSubmissionForm";
import VehicleCard from "@/components/VehicleCard";
import BookingDialog from "@/components/BookingDialog";

const Stat = ({ value, label, testid }) => (
  <div data-testid={testid}>
    <p className="font-display font-black text-3xl md:text-4xl text-white tabular-nums">
      {value}
    </p>
    <p className="mt-1 text-xs md:text-sm text-slate-400">{label}</p>
  </div>
);

const SERVICES = [
  {
    icon: KeyRound,
    title: "Vi köper ditt fordon",
    text: "Vi köper din husbil eller husvagn direkt. Betalning via banköverföring — du lämnar fordonet när pengarna finns på ditt konto. Enkelt och tryggt.",
    link: "/salj",
    linkLabel: "Sälj ditt fordon till oss",
  },
  {
    icon: Handshake,
    title: "Hitta din nästa",
    text: "Är du på jakt efter en husvagn eller husbil? Vi hittar rätt fordon till rätt pris, med ärlig rådgivning.",
  },
  {
    icon: Recycle,
    title: "Byt in & byt upp dig",
    text: "Byt in din nuvarande husbil, husvagn eller personbil som delbetalning och rulla hem i något nytt — enkelt och smidigt.",
  },
];

export default function Home() {
  const [vehicles, setVehicles] = useState([]);
  const { scrollY } = useScroll();
  const parallaxY = useTransform(scrollY, [0, 800], [0, 160]);

  useEffect(() => {
    api.get("/vehicles").then((r) => setVehicles(r.data)).catch(() => {});
  }, []);

  const available = vehicles.filter((v) => v.status !== "salt");
  const featured = (available.length ? available : vehicles).slice(0, 3);

  return (
    <main className="overflow-x-clip">
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden grain" data-testid="hero-section">
        <motion.div style={{ y: parallaxY }} className="absolute inset-0 scale-110">
          <img src={HERO_IMAGE} alt="" className="h-full w-full object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-ink via-ink/75 to-ink/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-transparent to-ink/80" />

        <div className="relative z-10 mx-auto max-w-7xl px-5 lg:px-8 pt-32 pb-20 w-full">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.6 }}
            className="font-mono-brand text-xs md:text-sm tracking-[0.3em] text-electric mb-6 flex items-center gap-2"
          >
            <MapPin size={14} />
            VIAREDSVÄGEN 43E · BORÅS
          </motion.p>

          <h1 className="font-display font-black tracking-tight leading-[0.95] text-[17vw] sm:text-7xl lg:text-[7.5rem] text-white">
            <MaskedLine delay={0.25}>
              KÖPER<span className="text-electric">.</span>
            </MaskedLine>
            <MaskedLine delay={0.4}>
              SÄLJER<span className="text-electric">.</span>
            </MaskedLine>
            <MaskedLine delay={0.55}>
              <span className="text-gradient-blue">
                BYTER<span className="text-electric">.</span>
              </span>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.9, duration: 0.7 }}
            className="mt-6 max-w-xl text-slate-300 text-base md:text-lg leading-relaxed"
            data-testid="hero-subline"
          >
            Husvagnar & husbilar i Borås. Personlig service från första visning till nyckelöverlämning —
            oavsett om du ska köpa, sälja eller byta.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.05, duration: 0.7 }}
            className="mt-8 flex flex-wrap items-center gap-4"
          >
            <Link
              to="/fordon"
              data-testid="hero-vehicles-button"
              className="inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 font-semibold text-white transition-colors hover:bg-brand-dark blue-glow"
            >
              Se våra fordon
              <ArrowRight size={17} />
            </Link>
            <a
              href="#salj"
              data-testid="hero-sell-button"
              className="inline-flex items-center gap-2 rounded-full border border-slate-500/60 px-7 py-3.5 font-semibold text-white transition-colors hover:border-electric hover:text-electric"
            >
              Sälj ditt fordon
            </a>
            <a
              href={`tel:${SITE.phone}`}
              data-testid="hero-phone-link"
              className="inline-flex items-center gap-2 px-2 py-2 font-mono-brand text-sm text-slate-300 hover:text-white transition-colors"
            >
              <Phone size={16} className="text-electric" />
              {SITE.phoneDisplay}
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.25, duration: 0.8 }}
            className="mt-16 grid grid-cols-2 gap-6 md:gap-10 max-w-md"
          >
            <Stat value={available.length} label="Tillgängliga fordon just nu" testid="stat-available" />
            <div data-testid="stat-fskatt">
              <BadgeCheck size={30} className="text-emerald-400" />
              <p className="mt-2 text-xs md:text-sm text-slate-300">Godkänd för F-skattsedel</p>
            </div>
          </motion.div>
        </div>
      </section>

      <Marquee />

      {/* SÄLJ TILL OSS */}
      <section id="salj" className="mx-auto max-w-7xl px-5 lg:px-8 pb-24 scroll-mt-24" data-testid="home-salj-section">
        <Reveal>
          <div className="grid gap-10 lg:grid-cols-[1fr_1.2fr] items-start rounded-3xl border border-brand/40 bg-gradient-to-br from-surface via-surface-2 to-surface p-8 md:p-12 grain">
            <div>
              <p className="font-mono-brand text-xs md:text-sm tracking-[0.3em] text-electric mb-4">SÄLJ TILL OSS</p>
              <h2 className="font-display font-extrabold tracking-tight text-2xl sm:text-3xl lg:text-4xl text-white">
                Sälj din husbil eller husvagn <span className="text-gradient-blue">direkt här</span>
              </h2>
              <p className="mt-4 text-slate-400 leading-relaxed">
                Fyll i regnumret, lägg gärna till foton och info — så kollar vi upp fordonet och ringer upp dig
                med ett prisförslag. Inget klickande runt, formuläret finns precis här.
              </p>
              <p className="mt-6 font-mono-brand text-sm text-slate-300">
                Eller ring oss: <a href="tel:+46720200996" className="text-electric hover:underline">+46 72 020 09 96</a>
              </p>
            </div>
            <VehicleSubmissionForm compact />
          </div>
        </Reveal>
      </section>

      {/* TJÄNSTER */}
      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-24 md:py-32" data-testid="services-section">
        <SectionHeading
          eyebrow="VÅRA TJÄNSTER"
          title="Köpa. Sälja. Byta. Vi hjälper dig hela vägen."
        />
        <div className="mt-12 grid gap-6 md:grid-cols-3">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.1} className="h-full">
              <div className="group h-full rounded-2xl border border-metallic/70 bg-surface p-8 transition-all duration-300 hover:border-brand/60 hover:bg-surface-2">
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-brand/15 border border-brand/30 text-electric transition-transform duration-300 group-hover:scale-110">
                  <s.icon size={22} />
                </span>
                <h3 className="mt-6 font-display font-bold text-xl text-white">{s.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-slate-400">{s.text}</p>
                {s.link && (
                  <Link
                    to={s.link}
                    data-testid="service-salj-link"
                    className="mt-5 inline-flex items-center gap-1.5 text-sm font-semibold text-electric hover:gap-3 transition-all"
                  >
                    {s.linkLabel}
                    <ArrowRight size={14} />
                  </Link>
                )}
              </div>
            </Reveal>
          ))}
        </div>
        <Reveal delay={0.2}>
          <div className="mt-6 flex flex-col md:flex-row md:items-center justify-between gap-6 rounded-2xl border border-electric/25 bg-gradient-to-r from-surface to-surface-2/70 p-8 electric-glow" data-testid="trust-card">
            <div className="flex items-start gap-4">
              <ShieldCheck size={28} className="text-electric shrink-0 mt-1" />
              <div>
                <p className="font-display font-bold text-lg text-white">Ett trygg affär</p>
                <p className="mt-1 text-sm text-slate-400">
                  {SITE.name} drivs av {SITE.owner} och är godkänd för F-skattsedel.
                </p>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <span className="inline-flex items-center gap-2 rounded-full bg-emerald-500/10 border border-emerald-400/30 px-4 py-2 text-sm text-emerald-300">
                <FileCheck2 size={15} />
                {SITE.fskatt}
              </span>
              <span className="rounded-full bg-surface-2/80 border border-metallic px-4 py-2 font-mono-brand text-xs text-slate-300">
                Org.nr {SITE.orgnr}
              </span>
            </div>
          </div>
        </Reveal>
      </section>

      {/* UTVALDA FORDON */}
      <section className="mx-auto max-w-7xl px-5 lg:px-8 pb-24 md:pb-32" data-testid="featured-section">
        <div className="flex items-end justify-between gap-6 flex-wrap">
          <SectionHeading eyebrow="I FOKUS JUST NU" title="Utvalda fordon" />
          <Reveal delay={0.1}>
            <Link
              to="/fordon"
              data-testid="featured-all-vehicles-link"
              className="inline-flex items-center gap-2 rounded-full border border-metallic px-5 py-2.5 text-sm font-medium text-slate-300 hover:border-brand hover:text-white transition-colors"
            >
              Alla fordon
              <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
        {featured.length === 0 ? (
          <Reveal>
            <p className="mt-10 rounded-2xl border border-dashed border-metallic p-10 text-center text-slate-400">
              Nya fordon publiceras löpande — hör av dig om du letar efter något särskilt.
            </p>
          </Reveal>
        ) : (
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {featured.map((v, i) => (
              <VehicleCard key={v.id} vehicle={v} index={i} />
            ))}
          </div>
        )}
      </section>

      {/* OM OSS TEASER */}
      <section className="relative border-y border-metallic/60 bg-surface/30" data-testid="about-teaser">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 py-24 md:py-32 grid gap-12 lg:grid-cols-2 items-center">
          <Reveal>
            <div className="relative">
              <img
                src={ABOUT_IMAGE}
                alt="Husvagn på väg mot äventyr"
                loading="lazy"
                className="rounded-2xl border border-metallic/70 w-full object-cover aspect-[4/3]"
              />
              <div className="absolute -bottom-5 -right-5 hidden md:block rounded-2xl bg-surface border border-metallic px-6 py-5 electric-glow">
                <p className="font-mono-brand text-[11px] tracking-[0.25em] text-slate-400 uppercase">Ägare</p>
                <p className="font-display font-bold text-lg text-white mt-1">{SITE.owner}</p>
              </div>
            </div>
          </Reveal>
          <div>
            <SectionHeading eyebrow="OM OSS" title="Ett personligt fritidsföretag i Borås" />
            <Reveal delay={0.1}>
              <p className="mt-6 text-slate-400 leading-relaxed">
                {SITE.name} drivs av {SITE.owner} — en person med ett genuint stort intresse för husbilar och
                för att göra riktigt bra affärer. Varje affär ska vara smidig och trygg: du ska känna dig
                välkommen, väl omhändertagen och alltid nöjd när du köper något av oss.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* HITTA HIT */}
      <section className="border-y border-metallic/60 bg-surface/30" data-testid="home-hitta-hit">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 py-24 md:py-32">
          <SectionHeading eyebrow="HITTA HIT" title="Vi finns på Viaredsvägen i Borås" />
          <div className="mt-12 grid gap-6 lg:grid-cols-[1.5fr_1fr] items-stretch">
            <Reveal className="h-full">
              <div className="h-full rounded-2xl overflow-hidden border border-metallic/70" data-testid="home-map">
                <iframe
                  title="Karta till Borås Fritid, Viaredsvägen 43E, Borås"
                  src={SITE.mapsEmbed}
                  className="map-frame w-full h-full min-h-[320px]"
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  allowFullScreen
                />
              </div>
            </Reveal>
            <div className="flex flex-col gap-6">
              <Reveal delay={0.1}>
                <div className="rounded-2xl border border-metallic/70 bg-surface p-8">
                  <MapPin size={24} className="text-electric" />
                  <h3 className="mt-4 font-display font-bold text-xl text-white">Adress</h3>
                  <p className="mt-2 text-slate-300 leading-relaxed">
                    {SITE.address}
                    <br />
                    {SITE.zip}
                  </p>
                  <a
                    href={SITE.mapsDirections}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="home-directions-button"
                    className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand px-6 py-3 text-sm font-semibold text-white hover:bg-brand-dark transition-colors"
                  >
                    Vägbeskrivning
                    <ArrowRight size={15} />
                  </a>
                </div>
              </Reveal>
              <Reveal delay={0.15}>
                <div className="rounded-2xl border border-metallic/70 bg-surface p-8">
                  <Phone size={24} className="text-electric" />
                  <h3 className="mt-4 font-display font-bold text-xl text-white">Öppettider</h3>
                  <p className="mt-2 inline-flex items-center rounded-full bg-brand/15 border border-brand/30 px-4 py-1.5 text-sm font-semibold text-electric" data-testid="home-hours-badge">
                    Endast tidsbokning · dygnet runt
                  </p>
                  <a
                    href={`tel:${SITE.phone}`}
                    data-testid="home-hittahit-phone"
                    className="mt-4 block font-mono-brand text-lg text-white hover:text-electric transition-colors"
                  >
                    {SITE.phoneDisplay}
                  </a>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-7xl px-5 lg:px-8 py-24 md:py-32">
        <Reveal>
          <div className="relative overflow-hidden rounded-3xl border border-brand/40 bg-gradient-to-br from-surface via-surface-2 to-surface p-10 md:p-16 text-center grain" data-testid="cta-section">
            <div className="absolute -top-24 left-1/2 -translate-x-1/2 h-64 w-[36rem] rounded-full bg-brand/25 blur-[100px] pointer-events-none" />
            <h2 className="relative font-display font-black tracking-tight text-3xl md:text-5xl text-white">
              Redo att hitta din nästa <span className="text-gradient-blue">fritidsbostad?</span>
            </h2>
            <p className="relative mt-4 text-slate-400 max-w-lg mx-auto">
              Ring oss direkt eller boka en visning — så visar vi våra fordon på {SITE.address}, {SITE.zip}.
            </p>
            <div className="relative mt-8 flex flex-wrap items-center justify-center gap-4">
              <a
                href={`tel:${SITE.phone}`}
                data-testid="cta-phone-button"
                className="inline-flex items-center gap-2 rounded-full bg-brand px-7 py-3.5 font-semibold text-white hover:bg-brand-dark transition-colors blue-glow"
              >
                <Phone size={17} />
                {SITE.phoneDisplay}
              </a>
              <a
                href="#salj"
                data-testid="cta-sell-button"
                className="rounded-full border border-slate-500/60 px-7 py-3.5 font-semibold text-white hover:border-electric hover:text-electric transition-colors"
              >
                Sälj ditt fordon
              </a>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
